/**
 * The filtered issues, as a single accordion — one column, not two.
 *
 * Replaces the old pairing of `FindingsList` (a listbox) beside `IssueDossier`
 * in its own column. That pairing put the clicked row and its detail two
 * columns apart, and for the year facet specifically it was actively
 * misleading: the list narrowed to one issue while the detail beside it kept
 * showing every year regardless, which is why the filter looked like it
 * wasn't working. A row's own detail now opens directly beneath its header —
 * adjacent, no separate pane to fall out of sync with the list above it.
 *
 * Exactly one row is open at a time, matching the "one issue open" invariant
 * the rest of this module already assumes. Opening a different row's header
 * closes whichever was open; the header row is `aria-expanded`, not
 * `aria-selected` — this is a disclosure list now, not a listbox.
 *
 * ── Keyboard model ────────────────────────────────────────────────────────────
 * Arrow keys move focus between HEADER rows only, never into an open row's own
 * body — Tab still reaches the evidence disclosure and the field chips inside
 * it normally. Enter/Space on the focused header TOGGLES that row: opens it if
 * closed, closes it if already open. Escape collapses whichever row is open
 * and returns focus to its header, from anywhere inside that row's body — the
 * same behaviour `IssueDossier`'s own Escape handler already gives it.
 *
 * The whole header column is ONE tab stop: a roving `tabIndex` follows the
 * focused row, exactly as `FindingsList` did.
 */

import type { KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { ValidationIssue } from '../../../types/dataQuality';
import { formatYearRange } from '../utils/dataQualityFormat';
import { isAmbiguous } from './dqRules';
import { SEVERITY_TOKENS, verdictToken } from './dqTokens';
import IssueDossier from './IssueDossier';

export interface FindingsAccordionListProps {
  companyId: string;
  source: string;
  issues: ValidationIssue[];
  selectedCode: string | null;
  onSelectCode: (code: string) => void;
  onCloseDossier: () => void;
  onSelectField?: (field: string) => void;
  /** The active fiscal-year facet, passed straight through to the open row. */
  yearFilter?: number[];
  /** How many issues this payload holds, before filtering. */
  shownCount: number;
  /** The backend's true distinct-issue count (`issue_count`). */
  totalCount: number;
  /** The backend's own count of issues it withheld. */
  truncatedCount?: number;
}

export default function FindingsAccordionList({
  companyId,
  source,
  issues,
  selectedCode,
  onSelectCode,
  onCloseDossier,
  onSelectField,
  yearFilter,
  shownCount,
  totalCount,
  truncatedCount,
}: FindingsAccordionListProps) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  // Which header the keyboard is on. Starts at the open row so returning to
  // the list resumes where the reader left it.
  const [focusIndex, setFocusIndex] = useState(0);

  useEffect(() => {
    const open = issues.findIndex((issue) => issue.code === selectedCode);
    if (open >= 0) setFocusIndex(open);
  }, [selectedCode, issues]);

  // Keep the roving index inside the list when a filter shrinks it, and drop
  // the refs of rows that no longer exist.
  useEffect(() => {
    refs.current.length = issues.length;
    setFocusIndex((current) => Math.min(current, Math.max(0, issues.length - 1)));
  }, [issues.length]);

  const moveTo = (index: number) => {
    const bounded = Math.max(0, Math.min(issues.length - 1, index));
    setFocusIndex(bounded);
    refs.current[bounded]?.focus();
  };

  const toggle = (code: string) => {
    if (code === selectedCode) onCloseDossier();
    else onSelectCode(code);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveTo(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveTo(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        moveTo(0);
        break;
      case 'End':
        event.preventDefault();
        moveTo(issues.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        toggle(issues[index].code);
        break;
      default:
        break;
    }
  };

  if (issues.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-neutral-200 py-10 text-center">
        <p className="text-sm text-neutral-500">No issues match this filter.</p>
      </div>
    );
  }

  // The backend's own number, not a comparison of two counts — `issue_count`
  // and the array length can differ for reasons other than truncation.
  const withheld = truncatedCount ?? 0;

  return (
    <div>
      <ul className="space-y-1.5">
        {issues.map((issue, index) => {
          const token = SEVERITY_TOKENS[issue.severity];
          const Icon = token.icon;
          const open = issue.code === selectedCode;
          const verdict = verdictToken(issue.ai_verdict);
          const ambiguous = isAmbiguous(issue.code);
          const panelId = `dq-finding-panel-${issue.code}`;

          return (
            <li
              key={issue.code}
              className="rounded-lg border border-neutral-200 overflow-hidden"
              // Escape collapses this row from ANYWHERE inside it — the
              // header (focus lands there right after a click) or the
              // dossier body (focus lands there after tabbing in). Listening
              // on the `<li>` catches both, rather than relying solely on
              // `IssueDossier`'s own handler, which only ever sees a keydown
              // whose target is inside its own subtree — never the sibling
              // header button that a plain click just focused.
              onKeyDown={(event) => {
                if (event.key === 'Escape' && open) {
                  event.stopPropagation();
                  onCloseDossier();
                  refs.current[index]?.focus();
                }
              }}
            >
              <button
                ref={(el) => {
                  refs.current[index] = el;
                }}
                type="button"
                aria-expanded={open}
                aria-controls={open ? panelId : undefined}
                // Roving tabindex — the header column is one tab stop.
                tabIndex={index === focusIndex ? 0 : -1}
                onFocus={() => setFocusIndex(index)}
                onClick={() => toggle(issue.code)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-500 ${
                  open ? 'bg-primary-50' : 'hover:bg-neutral-50'
                }`}
              >
                <Icon className={`h-4 w-4 shrink-0 ${token.text}`} aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate text-sm text-neutral-800">
                  {issue.label}
                </span>
                {ambiguous && (
                  <span
                    className="shrink-0 text-[10px] uppercase tracking-wide text-secondary-500"
                    title="This kind of finding can be a genuine business fact or an extraction fault — the AI reviewer is asked about it first."
                  >
                    ambiguous
                  </span>
                )}
                {verdict && (
                  <span
                    className={`h-1.5 w-1.5 shrink-0 rounded-full ${verdict.dot}`}
                    title={`AI verdict: ${verdict.label}`}
                  />
                )}
                <span className="shrink-0 text-xs text-neutral-500">
                  {formatYearRange(issue.years)}
                </span>
              </button>

              {open && (
                <div
                  id={panelId}
                  className="border-t border-neutral-200 bg-white px-3 py-3"
                >
                  <IssueDossier
                    companyId={companyId}
                    code={issue.code}
                    source={source}
                    onSelectField={onSelectField}
                    onClose={onCloseDossier}
                    yearFilter={yearFilter}
                    // The row's own header is the toggle and the border — the
                    // dossier drops its card shell and close button so there
                    // is only ever one collapse affordance per row.
                    bare
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {issues.length !== shownCount && (
        <p className="mt-2 text-[11px] text-neutral-400">
          Showing {issues.length} of {shownCount} issues held here.
        </p>
      )}

      {/* Never hidden by a filter, and never left dangling: a bare "18 further
          issues not listed" reads as a control the reader failed to find. */}
      {withheld > 0 && (
        <p className="mt-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs leading-relaxed text-neutral-600">
          This source raised <strong className="font-semibold">{totalCount}</strong> distinct
          issues and the stored summary returns the {shownCount} most severe, so {withheld}{' '}
          {withheld === 1 ? 'is' : 'are'} not listed here — and the filter counts beside them
          describe only the {shownCount} shown.
        </p>
      )}
    </div>
  );
}
